/**
 * Job Lookup Service
 * Provides flexible job lookup by UUID or public slug
 */

import supabase from './supabase';
import { getJobLookupField } from './slugGenerator';

/**
 * Get a job by UUID or public slug
 * @param {string} identifier - Job UUID or public slug
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export const getJobByIdOrSlug = async (identifier) => {
  try {
    if (!identifier) {
      return { data: null, error: { message: 'Identifier is required' } };
    }

    const lookupField = getJobLookupField(identifier);

    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        employer:user_profiles!jobs_employer_id_fkey (
          id,
          full_name,
          email,
          company_name
        )
      `)
      .eq(lookupField, identifier)
      .single();

    if (error) {
      console.error(`[getJobByIdOrSlug] Error fetching job with ${lookupField}=${identifier}:`, error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('[getJobByIdOrSlug] Unexpected error:', err);
    return { data: null, error: err };
  }
};

/**
 * Get multiple jobs by IDs/slugs
 * Note: This does two queries for simplicity. For large batches, optimize as needed.
 * @param {string[]} identifiers - Array of UUIDs or slugs
 * @returns {Promise<{data: object[], error: object|null}>}
 */
export const getJobsByIdOrSlug = async (identifiers) => {
  try {
    if (!identifiers || identifiers.length === 0) {
      return { data: [], error: null };
    }

    const uuids = identifiers.filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
    const slugs = identifiers.filter(id => !uuids.includes(id));

    let query = supabase.from('jobs').select('*');

    // Build the query with OR logic
    if (uuids.length > 0 && slugs.length > 0) {
      query = query.or(`id.in.(${uuids.map(id => `"${id}"`).join(',')}),public_slug.in.(${slugs.map(s => `"${s}"`).join(',')})`);
    } else if (uuids.length > 0) {
      query = query.in('id', uuids);
    } else if (slugs.length > 0) {
      query = query.in('public_slug', slugs);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[getJobsByIdOrSlug] Error fetching jobs:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[getJobsByIdOrSlug] Unexpected error:', err);
    return { data: [], error: err };
  }
};

/**
 * Update a job's public slug (rarely needed)
 * @param {string} jobId - Job UUID
 * @param {string} newSlug - New public slug
 * @returns {Promise<{success: boolean, error: object|null}>}
 */
export const updateJobSlug = async (jobId, newSlug) => {
  try {
    const { error } = await supabase
      .from('jobs')
      .update({ public_slug: newSlug })
      .eq('id', jobId);

    if (error) {
      console.error('[updateJobSlug] Error updating slug:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error('[updateJobSlug] Unexpected error:', err);
    return { success: false, error: err };
  }
};

/**
 * Check if a slug is available (not yet taken)
 * @param {string} slug - Slug to check
 * @returns {Promise<{available: boolean, error: object|null}>}
 */
export const isSlugAvailable = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('id', { count: 'exact' })
      .eq('public_slug', slug)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows found, which means slug is available
      console.error('[isSlugAvailable] Error checking slug:', error);
      return { available: false, error };
    }

    return { available: !data, error: null };
  } catch (err) {
    console.error('[isSlugAvailable] Unexpected error:', err);
    return { available: false, error: err };
  }
};

export default {
  getJobByIdOrSlug,
  getJobsByIdOrSlug,
  updateJobSlug,
  isSlugAvailable,
};
